import React, {useState, useEffect} from 'react'
import PropTypes from 'prop-types'
import {Content} from '@builder.io/sdk-react'
import {customComponents} from 'hec/builder'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {defaultDrawerChildren} from './utils'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import modelMap from 'hec/builder/model-map'
import {
    Drawer,
    DrawerHeader,
    DrawerTrigger,
    DrawerContent,
    DrawerTitle,
    DrawerDescription
} from '@/components/ui/drawer'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'

export function BuilderNavDrawer({children, navDrawerContent, attributes}) {
    const [open, setOpen] = useState(false)
    const config = getConfig()
    const shouldQueryDrawerData = !navDrawerContent?.value

    const {
        data: navDrawerData,
        isLoading: isNavDrawerDataLoading,
        error: navDrawerDataError
    } = useFetchBuilderReference({
        model: modelMap.navDrawerContent,
        shouldQuery: shouldQueryDrawerData,
        referenceId: navDrawerContent?.id
    })

    useEffect(() => {
        const handleNavDrawerClose = () => {
            setOpen(false)
        }

        document.addEventListener('closeNavDrawer', handleNavDrawerClose)

        return () => {
            document.removeEventListener('closeNavDrawer', handleNavDrawerClose)
        }
    }, [])

    const handleOpenChange = (open) => {
        setOpen(open)
        if (open) return
        const closeDrawerEvent = new CustomEvent('closeNavDrawer')
        document.dispatchEvent(closeDrawerEvent)
    }

    if (isNavDrawerDataLoading && shouldQueryDrawerData) {
        return <></>
    }

    if (navDrawerDataError) {
        console.error(navDrawerDataError)
        return <div>There was an error fetching Nav Drawer data.</div>
    }

    const navDrawerContentData = navDrawerContent?.value || navDrawerData

    return (
        <Drawer
            direction="left"
            open={open}
            onOpenChange={handleOpenChange}
            {...attributes}
            data-testid="navDrawer"
        >
            <DrawerTrigger data-testid="navDrawer-DrawerTrigger">{children}</DrawerTrigger>
            <DrawerContent data-testid="navDrawer-DrawerContent">
                <VisuallyHidden.Root>
                    <DrawerTitle data-testid="navDrawer-DrawerTitle" />
                    <DrawerDescription data-testid="navDrawer-DrawerDescription" />
                </VisuallyHidden.Root>
                <DrawerHeader data-testid="navDrawer-DrawerHeader" />
                {navDrawerContentData && customComponents && (
                    <Content
                        model={modelMap.navDrawerContent}
                        content={navDrawerContentData}
                        apiKey={config.app.builder.api}
                        customComponents={customComponents}
                        data-testid="navDrawer-Content"
                    />
                )}
            </DrawerContent>
        </Drawer>
    )
}

BuilderNavDrawer.propTypes = {
    children: PropTypes.node,
    navDrawerContent: PropTypes.any,
    attributes: PropTypes.object
}

export const NavDrawerDefinition = {
    component: BuilderNavDrawer,
    name: 'NavDrawer',
    image: 'https://unpkg.com/css.gg@2.0.0/icons/svg/menu.svg',
    noWrap: true,
    canHaveChildren: true,
    defaultChildren: [defaultDrawerChildren],
    inputs: [
        {
            name: 'navDrawerContent',
            type: 'reference',
            model: modelMap.navDrawerContent,
            required: true
        }
    ]
}
